/**
 * Bestpreisgarantie: wiederverwendbare Bausteine für Home, Produkt, Cart & Checkout.
 *
 * BestpreisHero      – Landingpage-Hero (Marketing-Kopf)
 * BestpreisRibbon    – Slim-CTA (Preis-Nähe auf Produktseite)
 * BestpreisReminder  – kompaktes Reassurance-Banner (Cart/Checkout/Angebot)
 * BestpreisForm      – Anfrageformular mit Datei-Upload
 * BESTPREIS_TRUST    – Trust-Icons (wiederverwendbar)
 */
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { adsConversion } from "@/lib/ads";
import axios from "axios";
import {
  ShieldCheck, Percent, MapPin, Wrench, LifeBuoy, HeartHandshake,
  ArrowRight, Upload, FileText, X, Loader2, Check, Sparkles, Building2,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Springt zuverlässig zum Bestpreis-Formular (Anker `#anfrage`),
 * egal ob der User bereits auf /bestpreisgarantie ist oder nicht.
 * - Nicht auf /bestpreisgarantie → navigate(`/bestpreisgarantie?produkt=…#anfrage`)
 * - Bereits dort → smooth scroll zum Element (React Router löst same-route
 *   Link nicht aus → sonst „Klick ohne Reaktion"-Bug).
 */
export function useBestpreisAnchor() {
  const location = useLocation();
  const navigate = useNavigate();
  return React.useCallback((productName) => {
    const q = productName ? `?produkt=${encodeURIComponent(productName)}` : "";
    if (location.pathname === "/bestpreisgarantie") {
      const el = document.getElementById("anfrage");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        // Fokus in erstes Feld setzen, ohne den Scroll zu unterbrechen.
        window.setTimeout(() => {
          const input = el.querySelector('input[data-testid="bp-name"]');
          if (input) input.focus({ preventScroll: true });
        }, 600);
      } else {
        // Fallback: hart nachladen, sollte praktisch nie eintreten
        navigate(`/bestpreisgarantie${q}#anfrage`);
      }
    } else {
      navigate(`/bestpreisgarantie${q}#anfrage`);
    }
  }, [location.pathname, navigate]);
}

export const BESTPREIS_TRUST = [
  { icon: Building2,      title: "Deutscher Fachbetrieb",       text: "Persönlicher Ansprechpartner statt anonymem Online-Shop, erreichbar per Telefon, Mail und WhatsApp." },
  { icon: MapPin,         title: "Werkstatt im Ruhrgebiet",     text: "Beratung, Reparatur und technische Betreuung persönlich vor Ort in Bottrop." },
  { icon: LifeBuoy,       title: "Reparatur-Service",           text: "Wenn später etwas defekt ist, haben Sie einen Ansprechpartner, Sie müssen nicht selbst nach einer Lösung suchen." },
  { icon: Wrench,         title: "Erfahrung aus der Vermietung", text: "Wir kennen Fotobox-Systeme nicht nur aus dem Verkauf, sondern aus dem täglichen Veranstaltungsbetrieb." },
  { icon: HeartHandshake, title: "Persönlicher Support",        text: "Auch nach dem Kauf bleiben wir Ansprechpartner für technische und praktische Fragen." },
  { icon: ShieldCheck,    title: "Gesetzliche Mängelhaftung",   text: "Für Verbraucherkäufe beträgt die gesetzliche Verjährungsfrist bei beweglichen Sachen zwei Jahre ab Ablieferung." },
];

// -------------------------------------------------------------
// Slim CTA-Ribbon: wird direkt am/unter dem Preis auf Produkt-
// Detailseiten angezeigt.
// -------------------------------------------------------------
export function BestpreisRibbon({ productName, size = "md" }) {
  const pad = size === "sm" ? "px-3 py-2" : "px-4 py-3";
  const openForm = useBestpreisAnchor();
  return (
    <button
      type="button"
      onClick={() => openForm(productName)}
      data-testid="bestpreis-ribbon"
      className={`group relative inline-flex items-center gap-3 rounded-xl border border-brand-yellow/50 bg-brand-yellow/10 ${pad} text-left w-full hover:bg-brand-yellow/15 transition-colors`}
    >
      <div className="w-9 h-9 rounded-full bg-brand-yellow/25 flex items-center justify-center shrink-0">
        <Percent className="w-4 h-4 text-brand-yellow" strokeWidth={2.4} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] tracking-[0.22em] uppercase text-brand-yellow font-semibold">Bestpreisgarantie</div>
        <div className="text-sm text-ink leading-snug">
          Günstigeres vergleichbares Angebot? <b>Wir prüfen es und erstellen ein Gegenangebot.</b>
        </div>
      </div>
      <ArrowRight className="w-4 h-4 text-ink-soft group-hover:text-ink transition-colors shrink-0" />
    </button>
  );
}

// -------------------------------------------------------------
// Kompaktes Reminder-Banner: für Cart, Checkout, Angebots-Seite.
// -------------------------------------------------------------
export function BestpreisReminder({ variant = "light", className = "" }) {
  const dark = variant === "dark";
  const openForm = useBestpreisAnchor();
  return (
    <div
      data-testid="bestpreis-reminder"
      className={`rounded-2xl p-5 md:p-6 flex flex-wrap items-center justify-between gap-4 ${dark ? "bg-ink text-white border border-white/10" : "bg-warm border border-brand-yellow/40"} ${className}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${dark ? "bg-brand-yellow/25" : "bg-brand-yellow/25"}`}>
          <ShieldCheck className="w-5 h-5 text-brand-yellow" strokeWidth={2.2} />
        </div>
        <div>
          <div className={`text-[10px] tracking-[0.22em] uppercase font-semibold ${dark ? "text-brand-yellow" : "text-brand-yellow"}`}>
            Bestpreisgarantie
          </div>
          <div className={`text-base md:text-lg leading-snug mt-1 ${dark ? "text-white" : "text-ink"}`}>
            Günstigeres vergleichbares Angebot? <b>Wir prüfen es</b> und erstellen Ihnen ein Gegenangebot, mit den Vorteilen eines deutschen Fachbetriebs.
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={() => openForm()}
        className={dark ? "btn-accent" : "btn-accent"}
        data-testid="bestpreis-reminder-cta"
      >
        Bestpreis prüfen lassen <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// -------------------------------------------------------------
// Hero-Section: Marketing-Kopf auf Home & /bestpreisgarantie.
// -------------------------------------------------------------
export function BestpreisHero({ compact = false }) {
  const openForm = useBestpreisAnchor();
  return (
    <div
      data-testid="bestpreis-hero"
      className="contrast-dark relative overflow-hidden rounded-3xl p-8 md:p-12 lg:p-16 border border-brand-yellow/25"
      style={{
        background: "linear-gradient(135deg, #14120F 0%, #1F1B15 55%, #2A2118 100%)",
      }}
    >
      <div aria-hidden className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(60% 60% at 82% 20%, rgba(244,184,74,0.32) 0%, transparent 60%), radial-gradient(50% 50% at 8% 82%, rgba(232,168,61,0.18) 0%, transparent 65%)" }}
      />
      <div className="relative grid grid-cols-1 lg:grid-cols-[1.4fr,1fr] gap-8 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-yellow/15 border border-brand-yellow/40 text-brand-yellow text-[10px] tracking-[0.24em] uppercase font-semibold mb-6">
            <Sparkles className="w-3 h-3" /> Bestpreisgarantie
          </div>
          <h2 className={`font-heading text-white leading-[1.02] ${compact ? "text-3xl md:text-4xl" : "text-4xl md:text-5xl lg:text-6xl"}`}>
            Günstigeres vergleichbares Angebot?{" "}
            <span className="font-serif-italic text-brand-yellow">Wir prüfen es und legen nach.</span>
          </h2>
          <p className="mt-5 max-w-xl text-lg text-white/80 leading-relaxed">
            Senden Sie uns Ihr günstigeres Vergleichsangebot als PDF, Foto oder Link.
            Wir prüfen Ausstattung, Lieferumfang und Preis, und erstellen Ihnen
            innerhalb von 24 Stunden ein Gegenangebot. Und Sie verzichten trotzdem
            nicht auf die Vorteile eines deutschen Fachbetriebs mit Werkstatt,
            Support und Erfahrung aus der Fotobox-Vermietung.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => openForm()} className="btn-accent !py-4 !px-7 shadow-2xl shadow-brand-yellow/40" data-testid="bestpreis-hero-cta">
              Jetzt Bestpreis anfordern <ArrowRight className="w-4 h-4" />
            </button>
            <a href="tel:020417355679" className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-white/25 text-white hover:bg-white/10 transition-colors text-sm font-medium">
              02041 7355679 anrufen
            </a>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/70">
            <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-brand-yellow" strokeWidth={3} /> Antwort in 24 Stunden</span>
            <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-brand-yellow" strokeWidth={3} /> Kostenlos & unverbindlich</span>
            <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-brand-yellow" strokeWidth={3} /> Werkstatt Ruhrgebiet</span>
          </div>
        </div>
        {!compact && (
          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute inset-0 rounded-2xl blur-3xl opacity-40" style={{ background: "radial-gradient(60% 60% at 50% 50%, rgba(244,184,74,0.5) 0%, transparent 70%)" }} />
              <div className="relative rounded-2xl bg-white/5 border border-brand-yellow/25 backdrop-blur p-6">
                <div className="text-[10px] tracking-[0.22em] uppercase text-brand-yellow font-semibold">So funktioniert's</div>
                {[
                  { n: 1, t: "Angebot einreichen", s: "PDF, Foto oder Link hochladen" },
                  { n: 2, t: "Wir prüfen persönlich", s: "Fachhandel statt Algorithmus" },
                  { n: 3, t: "Gegenangebot in 24 h", s: "Individuell nach Prüfung" },
                ].map((s) => (
                  <div key={s.n} className="mt-4 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-brand-yellow text-ink font-heading font-semibold flex items-center justify-center shrink-0">{s.n}</div>
                    <div>
                      <div className="text-white font-medium">{s.t}</div>
                      <div className="text-white/70 text-sm">{s.s}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Anfrageformular mit Datei-Upload
// -------------------------------------------------------------
export function BestpreisForm({ presetProduct = "", onSuccess }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    desired_product: presetProduct,
    competitor_url: "", competitor_price: "", message: "",
    consent: false,
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleFile = (f) => {
    if (!f) { setFile(null); return; }
    const ok = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!ok.includes(f.type)) { toast.error("Nur PDF, JPG, PNG oder WEBP erlaubt"); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error("Max. 15 MB pro Datei"); return; }
    setFile(f);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.email.trim() || !form.desired_product.trim()) {
      toast.error("Bitte Name, E-Mail und Wunsch-System ausfüllen");
      return;
    }
    if (!file && !form.competitor_url.trim()) {
      toast.error("Bitte laden Sie das Konkurrenz-Angebot hoch ODER geben Sie einen Link an");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? (v ? "1" : "0") : v));
      if (file) fd.append("file", file);
      const r = await axios.post(`${BACKEND}/api/bestpreis/inquiry`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      adsConversion("anfrage");
      toast.success("Ihre Bestpreis-Anfrage ist eingegangen. Wir melden uns in 24 Stunden.");
      setForm({ name: "", email: "", phone: "", company: "", desired_product: "",
        competitor_url: "", competitor_price: "", message: "", consent: false });
      setFile(null);
      onSuccess?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Absenden. Bitte erneut versuchen.");
    } finally {
      setSubmitting(false);
    }
  };

  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <form onSubmit={submit} className="card-clean p-6 md:p-8" data-testid="bestpreis-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input required placeholder="Ihr Name *" value={form.name} onChange={on("name")}
          className="border border-hair rounded-md px-4 py-3 text-sm focus:border-brand focus:outline-none" data-testid="bp-name" />
        <input required type="email" placeholder="E-Mail *" value={form.email} onChange={on("email")}
          className="border border-hair rounded-md px-4 py-3 text-sm focus:border-brand focus:outline-none" data-testid="bp-email" />
        <input placeholder="Telefon (optional)" value={form.phone} onChange={on("phone")}
          className="border border-hair rounded-md px-4 py-3 text-sm focus:border-brand focus:outline-none" data-testid="bp-phone" />
        <input placeholder="Firma (optional)" value={form.company} onChange={on("company")}
          className="border border-hair rounded-md px-4 py-3 text-sm focus:border-brand focus:outline-none" data-testid="bp-company" />
        <input required placeholder="Wunsch-System *" value={form.desired_product} onChange={on("desired_product")}
          className="md:col-span-2 border border-hair rounded-md px-4 py-3 text-sm focus:border-brand focus:outline-none" data-testid="bp-product" />
        <input placeholder="Konkurrenz-Link (URL zum günstigeren Angebot)" value={form.competitor_url} onChange={on("competitor_url")}
          className="border border-hair rounded-md px-4 py-3 text-sm focus:border-brand focus:outline-none" data-testid="bp-competitor-url" />
        <input placeholder="Preis vom Mitbewerber (z. B. 2.499 €)" value={form.competitor_price} onChange={on("competitor_price")}
          className="border border-hair rounded-md px-4 py-3 text-sm focus:border-brand focus:outline-none" data-testid="bp-competitor-price" />
        <textarea rows={4} placeholder="Anmerkung (optional)" value={form.message} onChange={on("message")}
          className="md:col-span-2 border border-hair rounded-md px-4 py-3 text-sm focus:border-brand focus:outline-none" data-testid="bp-message" />
      </div>

      {/* Datei-Upload */}
      <div className="mt-4">
        <div className="text-xs uppercase tracking-widest text-ink-muted mb-2">Konkurrenz-Angebot als Beleg</div>
        {!file ? (
          <label className="flex items-center justify-center gap-3 border-2 border-dashed border-brand-yellow/50 rounded-xl px-5 py-6 cursor-pointer hover:bg-warm transition-colors" data-testid="bp-file-drop">
            <Upload className="w-5 h-5 text-brand-yellow" />
            <div className="text-sm text-ink">
              <div><b>Datei auswählen</b>, PDF, JPG, PNG oder WEBP</div>
              <div className="text-xs text-ink-muted mt-0.5">max. 15 MB · alternativ Link oben eintragen</div>
            </div>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
              className="hidden" onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="bp-file-input" />
          </label>
        ) : (
          <div className="flex items-center justify-between border border-brand-yellow/40 bg-brand-yellow/10 rounded-xl px-4 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <FileText className="w-4 h-4 text-brand-yellow shrink-0" />
              <div className="min-w-0">
                <div className="text-sm text-ink truncate">{file.name}</div>
                <div className="text-xs text-ink-muted">{(file.size / 1024).toFixed(0)} KB</div>
              </div>
            </div>
            <button type="button" onClick={() => setFile(null)} className="text-ink-muted hover:text-ink" data-testid="bp-file-remove">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <label className="flex items-start gap-2 mt-5 text-xs text-ink-soft cursor-pointer">
        <input type="checkbox" checked={form.consent}
          onChange={(e) => setForm({ ...form, consent: e.target.checked })}
          className="mt-0.5" data-testid="bp-consent" />
        <span>Ich stimme zu, dass fotoboxverkauf.de meine Anfrage bearbeitet und mich kontaktiert. Details in der Datenschutzerklärung.</span>
      </label>

      <button type="submit" disabled={submitting || !form.consent}
        className="btn-accent w-full justify-center mt-5 disabled:opacity-60"
        data-testid="bp-submit">
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Wird gesendet…</> :
          <>Bestpreis-Anfrage absenden <ArrowRight className="w-4 h-4" /></>}
      </button>
      <div className="mt-3 text-xs text-ink-muted text-center">
        Antwort binnen 24 Stunden · Kostenlos & unverbindlich
      </div>
    </form>
  );
}
